import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, X, ImageIcon, Plus, Trash2, Upload } from 'lucide-react';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import {
  useGetActiveCategoriesQuery,
  useCreateProductMutation,
} from '../../app/api';

const ProductAdd = () => {
  const navigate = useNavigate();

  // ✅ RTK Query
  const { data: catData, isLoading: catLoading } = useGetActiveCategoriesQuery({ limit: 100 });
  const [createProduct, { isLoading: saving }] = useCreateProductMutation();

  const categories = catData?.data?.categories || [];

  const [formData, setFormData] = useState({
    name: '', slug: '', sku: '', shortDescription: '', description: '',
    brand: '', category_id: '', tags: '', collectionName: '',
    mrp: '', price: '', discount: '', discountType: 'percentage',
    costPrice: '', tax: '', taxIncluded: true, currency: 'INR',
    stock: '', lowStockAlert: 5, stockStatus: 'in_stock', maxOrderQuantity: 10,
    image: null, imagePreview: null,
    gallery: [], galleryPreviews: [],
    video: '', thumbnail: null, thumbnailPreview: null,
    hasVariants: false, variants: [], attributes: [],
    weight: '', dimensions: { length: 0, width: 0, height: 0 },
    shippingCharge: 0, freeShipping: false,
    deliveryTime: '3-5 days', shippingType: 'standard',
    returnable: true, returnDays: 7, replacement: true,
    warranty: '', warrantyPeriod: 0,
    sellerName: '', isCashOnDelivery: true,
    metaTitle: '', metaDescription: '', metaKeywords: '',
    isActive: true, isFeatured: false, isTrending: false, isNewArrival: false,
    isBestSeller: false, isRecommended: false,
    status: 'published', approvedByAdmin: false,
    offerText: '', couponCode: '',
    flashSale: { isActive: false, startDate: '', endDate: '', salePrice: 0 },
    manufacturerName: '', manufacturerAddress: '',
    countryOfOrigin: 'India', hsnCode: '',
  });
  const [errors, setErrors] = useState({});

  const generateSlug = (name) =>
    name.toLowerCase().trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const d = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'name') {
        d.slug = generateSlug(value);
        if (!prev.sku) d.sku = value.toUpperCase().replace(/\s+/g, '-');
      }
      return d;
    });
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData((p) => ({ ...p, [parent]: { ...p[parent], [field]: value } }));
  };

  const validateFile = (file) => {
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/jpg'].includes(file.type)) {
      toast.error('Only JPEG, PNG, WEBP allowed');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max 5MB per image');
      return false;
    }
    return true;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file || !validateFile(file)) return;
    const r = new FileReader();
    r.onloadend = () =>
      setFormData((prev) => ({ ...prev, image: file, imagePreview: r.result }));
    r.readAsDataURL(file);
  };

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files).filter(validateFile);
    if (!files.length) return;
    Promise.all(
      files.map(
        (file) =>
          new Promise((resolve) => {
            const r = new FileReader();
            r.onloadend = () => resolve({ file, preview: r.result });
            r.readAsDataURL(file);
          })
      )
    ).then((results) => {
      setFormData((prev) => ({
        ...prev,
        gallery: [...prev.gallery, ...results.map((r) => r.file)],
        galleryPreviews: [...prev.galleryPreviews, ...results.map((r) => r.preview)],
      }));
    });
  };

  const removeGalleryImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index),
      galleryPreviews: prev.galleryPreviews.filter((_, i) => i !== index),
    }));
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file || !validateFile(file)) return;
    const r = new FileReader();
    r.onloadend = () =>
      setFormData((prev) => ({ ...prev, thumbnail: file, thumbnailPreview: r.result }));
    r.readAsDataURL(file);
  };

  const addVariant = () =>
    setFormData((p) => ({
      ...p,
      variants: [...p.variants, { variantName: '', variantValue: '', price: '', stock: '', sku: '' }],
    }));
  const updateVariant = (i, f, v) =>
    setFormData((p) => ({
      ...p,
      variants: p.variants.map((x, idx) => (idx === i ? { ...x, [f]: v } : x)),
    }));
  const removeVariant = (i) =>
    setFormData((p) => ({ ...p, variants: p.variants.filter((_, idx) => idx !== i) }));

  const addAttribute = () =>
    setFormData((p) => ({ ...p, attributes: [...p.attributes, { key: '', value: '' }] }));
  const updateAttribute = (i, f, v) =>
    setFormData((p) => ({
      ...p,
      attributes: p.attributes.map((x, idx) => (idx === i ? { ...x, [f]: v } : x)),
    }));
  const removeAttribute = (i) =>
    setFormData((p) => ({ ...p, attributes: p.attributes.filter((_, idx) => idx !== i) }));

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = 'Product name is required';
    if (!formData.category_id) e.category_id = 'Category is required';
    if (!formData.description.trim()) e.description = 'Description is required';
    if (!formData.sku.trim()) e.sku = 'SKU is required';
    if (!formData.price || Number(formData.price) <= 0) e.price = 'Valid price required';
    if (formData.mrp && Number(formData.mrp) < Number(formData.price))
      e.mrp = 'MRP must be >= price';
    if (!formData.stock || Number(formData.stock) < 0) e.stock = 'Valid stock required';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fix the errors');
      const firstError = Object.keys(newErrors)[0];
      const el = document.querySelector(`[name="${firstError}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    try {
      const fd = new FormData();

      // Basic
      ['name', 'slug', 'sku', 'shortDescription', 'description', 'brand', 'collectionName'].forEach((k) =>
        fd.append(k, formData[k] || '')
      );
      fd.append('category_id', formData.category_id);
      if (formData.tags) {
        const tagsArr = formData.tags.split(',').map((t) => t.trim()).filter(Boolean);
        fd.append('tags', JSON.stringify(tagsArr));
      }

      // Pricing
      fd.append('mrp', formData.mrp || 0);
      fd.append('price', formData.price);
      fd.append('discount', formData.discount || 0);
      fd.append('discountType', formData.discountType);
      fd.append('costPrice', formData.costPrice || 0);
      fd.append('tax', formData.tax || 0);
      fd.append('taxIncluded', formData.taxIncluded);
      fd.append('currency', formData.currency);

      // Stock
      fd.append('stock', formData.stock);
      fd.append('lowStockAlert', formData.lowStockAlert);
      fd.append('stockStatus', formData.stockStatus);
      fd.append('maxOrderQuantity', formData.maxOrderQuantity);

      // Files
      if (formData.image) fd.append('image', formData.image);
      formData.gallery.forEach((f) => fd.append('gallery', f));
      if (formData.thumbnail) fd.append('thumbnail', formData.thumbnail);
      fd.append('video', formData.video || '');

      // Variants/Attributes
      fd.append('hasVariants', formData.hasVariants);
      if (formData.variants.length) {
        const cleaned = formData.variants.filter((v) => v.variantName && v.variantValue);
        fd.append('variants', JSON.stringify(cleaned));
      }
      if (formData.attributes.length) {
        const cleaned = formData.attributes.filter((a) => a.key && a.value);
        fd.append('attributes', JSON.stringify(cleaned));
      }

      // Shipping
      fd.append('weight', formData.weight || 0);
      fd.append('dimensions', JSON.stringify(formData.dimensions));
      fd.append('shippingCharge', formData.shippingCharge);
      fd.append('freeShipping', formData.freeShipping);
      fd.append('deliveryTime', formData.deliveryTime);
      fd.append('shippingType', formData.shippingType);

      // Return
      fd.append('returnable', formData.returnable);
      fd.append('returnDays', formData.returnDays);
      fd.append('replacement', formData.replacement);
      fd.append('warranty', formData.warranty || '');
      fd.append('warrantyPeriod', formData.warrantyPeriod);

      // Seller
      fd.append('sellerName', formData.sellerName || '');
      fd.append('isCashOnDelivery', formData.isCashOnDelivery);

      // SEO
      fd.append('metaTitle', formData.metaTitle || '');
      fd.append('metaDescription', formData.metaDescription || '');
      if (formData.metaKeywords) {
        const arr = formData.metaKeywords.split(',').map((k) => k.trim()).filter(Boolean);
        fd.append('metaKeywords', JSON.stringify(arr));
      }

      // Flags
      [
        'isActive', 'isFeatured', 'isTrending', 'isNewArrival',
        'isBestSeller', 'isRecommended', 'approvedByAdmin',
      ].forEach((k) => fd.append(k, formData[k]));
      fd.append('status', formData.status);

      // Offers
      fd.append('offerText', formData.offerText || '');
      fd.append('couponCode', formData.couponCode || '');
      fd.append('flashSale', JSON.stringify(formData.flashSale));

      // Manufacturer
      ['manufacturerName', 'manufacturerAddress', 'countryOfOrigin', 'hsnCode'].forEach((k) =>
        fd.append(k, formData[k] || '')
      );

      await createProduct(fd).unwrap();
      toast.success('Product created successfully!');
      navigate('/products');
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to create product');
    }
  };

  return (
    <Layout>
      <div className="add-product-page">
        <div className="page-header">
          <div>
            <Link
              to="/products"
              className="btn btn-sm btn-outline btn-back"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}
            >
              <ArrowLeft size={16} />
              <span>Back to Products</span>
            </Link>
            <h1 className="page-title">Add New Product</h1>
            <p className="page-subtitle">Create a new product for your store</p>
          </div>
        </div>

        <div className="card">
          <form onSubmit={handleSubmit}>
            {/* BASIC INFO */}
            <div className="form-section">
              <h3 className="form-section-title">Basic Information</h3>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Product Name <span className="required">*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`form-control ${errors.name ? 'error' : ''}`}
                    placeholder="Enter product name"
                  />
                  {errors.name && <div className="form-error">{errors.name}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">Category <span className="required">*</span></label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleChange}
                    className={`form-control ${errors.category_id ? 'error' : ''}`}
                    disabled={catLoading}
                  >
                    <option value="">{catLoading ? 'Loading...' : 'Select Category'}</option>
                    {categories.map((cat) => (
                      <option key={cat._id} value={cat._id}>{cat.name}</option>
                    ))}
                  </select>
                  {errors.category_id && <div className="form-error">{errors.category_id}</div>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Slug</label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    readOnly
                    className="form-control"
                    style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">SKU <span className="required">*</span></label>
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleChange}
                    className={`form-control ${errors.sku ? 'error' : ''}`}
                  />
                  {errors.sku && <div className="form-error">{errors.sku}</div>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Brand</label>
                  <input type="text" name="brand" value={formData.brand} onChange={handleChange} className="form-control" />
                </div>
                <div className="form-group">
                  <label className="form-label">Collection Name</label>
                  <input type="text" name="collectionName" value={formData.collectionName} onChange={handleChange} className="form-control" />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Tags</label>
                <input type="text" name="tags" value={formData.tags} onChange={handleChange} className="form-control" placeholder="herbal, organic, tea" />
              </div>

              <div className="form-group">
                <label className="form-label">Short Description</label>
                <input type="text" name="shortDescription" value={formData.shortDescription} onChange={handleChange} className="form-control" maxLength={500} />
              </div>

              <div className="form-group">
                <label className="form-label">Description <span className="required">*</span></label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className={`form-control ${errors.description ? 'error' : ''}`}
                  rows="5"
                />
                {errors.description && <div className="form-error">{errors.description}</div>}
              </div>
            </div>

            {/* PRICING */}
            <div className="form-section">
              <h3 className="form-section-title">Pricing & Stock</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Price (₹) <span className="required">*</span></label>
                  <input type="number" name="price" value={formData.price} onChange={handleChange} className={`form-control ${errors.price ? 'error' : ''}`} min="0" step="0.01" />
                  {errors.price && <div className="form-error">{errors.price}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">MRP (₹)</label>
                  <input type="number" name="mrp" value={formData.mrp} onChange={handleChange} className={`form-control ${errors.mrp ? 'error' : ''}`} min="0" />
                  {errors.mrp && <div className="form-error">{errors.mrp}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Cost Price (₹)</label>
                  <input type="number" name="costPrice" value={formData.costPrice} onChange={handleChange} className="form-control" min="0" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Discount</label>
                  <input type="number" name="discount" value={formData.discount} onChange={handleChange} className="form-control" min="0" />
                </div>
                <div className="form-group">
                  <label className="form-label">Discount Type</label>
                  <select name="discountType" value={formData.discountType} onChange={handleChange} className="form-control">
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed (₹)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Tax (%)</label>
                  <input type="number" name="tax" value={formData.tax} onChange={handleChange} className="form-control" min="0" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Stock <span className="required">*</span></label>
                  <input type="number" name="stock" value={formData.stock} onChange={handleChange} className={`form-control ${errors.stock ? 'error' : ''}`} min="0" />
                  {errors.stock && <div className="form-error">{errors.stock}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Low Stock Alert</label>
                  <input type="number" name="lowStockAlert" value={formData.lowStockAlert} onChange={handleChange} className="form-control" min="0" />
                </div>
                <div className="form-group">
                  <label className="form-label">Max Order Qty</label>
                  <input type="number" name="maxOrderQuantity" value={formData.maxOrderQuantity} onChange={handleChange} className="form-control" min="1" />
                </div>
              </div>
            </div>

            {/* MEDIA */}
            <div className="form-section">
              <h3 className="form-section-title">Media</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Main Image</label>
                  <div className="image-upload-container">
                    {formData.imagePreview ? (
                      <div className="image-preview-wrapper">
                        <img src={formData.imagePreview} alt="Preview" className="image-preview" />
                        <button type="button" className="remove-image-btn" onClick={() => setFormData((p) => ({ ...p, image: null, imagePreview: null }))}>
                          <X size={18} />
                        </button>
                      </div>
                    ) : (
                      <div className="image-upload-area">
                        <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} id="main-image" />
                        <label htmlFor="main-image" style={{ cursor: 'pointer' }}>
                          <ImageIcon size={32} className="upload-icon" />
                          <p className="upload-text">Upload Main Image</p>
                          <span className="upload-hint">PNG, JPG, WEBP (Max 5MB)</span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Thumbnail</label>
                  <div className="image-upload-container">
                    {formData.thumbnailPreview ? (
                      <div className="image-preview-wrapper">
                        <img src={formData.thumbnailPreview} alt="Thumbnail" className="image-preview" />
                        <button type="button" className="remove-image-btn" onClick={() => setFormData((p) => ({ ...p, thumbnail: null, thumbnailPreview: null }))}>
                          <X size={18} />
                        </button>
                      </div>
                    ) : (
                      <div className="image-upload-area">
                        <input type="file" accept="image/*" onChange={handleThumbnailChange} style={{ display: 'none' }} id="thumbnail-upload" />
                        <label htmlFor="thumbnail-upload" style={{ cursor: 'pointer' }}>
                          <ImageIcon size={32} className="upload-icon" />
                          <p className="upload-text">Upload Thumbnail</p>
                          <span className="upload-hint">PNG, JPG, WEBP</span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Gallery Images</label>
                <div className="gallery-upload-area">
                  <input type="file" accept="image/*" multiple onChange={handleGalleryChange} style={{ display: 'none' }} id="gallery-upload" />
                  <label htmlFor="gallery-upload" className="gallery-upload-btn">
                    <Upload size={20} />
                    <span>Add Gallery Images</span>
                  </label>
                </div>
                {formData.galleryPreviews.length > 0 && (
                  <div className="gallery-grid">
                    {formData.galleryPreviews.map((preview, index) => (
                      <div key={index} className="gallery-item">
                        <img src={preview} alt={`Gallery ${index + 1}`} />
                        <button type="button" className="remove-image-btn" onClick={() => removeGalleryImage(index)}>
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Video URL</label>
                <input type="url" name="video" value={formData.video} onChange={handleChange} className="form-control" placeholder="https://youtube.com/..." />
              </div>
            </div>

            {/* VARIANTS */}
            <div className="form-section">
              <h3 className="form-section-title">Variants & Attributes</h3>
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" name="hasVariants" checked={formData.hasVariants} onChange={handleChange} />
                This product has variants
              </label>

              <div className="form-group" style={{ marginTop: 12 }}>
                <div className="section-header-inline">
                  <label className="form-label">Variants</label>
                  <button type="button" className="btn btn-sm btn-outline" onClick={addVariant}>
                    <Plus size={14} /> Add Variant
                  </button>
                </div>
                {formData.variants.map((v, i) => (
                  <div key={i} className="variant-row">
                    <input type="text" placeholder="Name" value={v.variantName} onChange={(e) => updateVariant(i, 'variantName', e.target.value)} className="form-control" />
                    <input type="text" placeholder="Value" value={v.variantValue} onChange={(e) => updateVariant(i, 'variantValue', e.target.value)} className="form-control" />
                    <input type="number" placeholder="Price" value={v.price} onChange={(e) => updateVariant(i, 'price', e.target.value)} className="form-control" />
                    <input type="number" placeholder="Stock" value={v.stock} onChange={(e) => updateVariant(i, 'stock', e.target.value)} className="form-control" />
                    <input type="text" placeholder="SKU" value={v.sku} onChange={(e) => updateVariant(i, 'sku', e.target.value)} className="form-control" />
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => removeVariant(i)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="form-group">
                <div className="section-header-inline">
                  <label className="form-label">Attributes</label>
                  <button type="button" className="btn btn-sm btn-outline" onClick={addAttribute}>
                    <Plus size={14} /> Add Attribute
                  </button>
                </div>
                {formData.attributes.map((a, i) => (
                  <div key={i} className="attribute-row">
                    <input type="text" placeholder="Key" value={a.key} onChange={(e) => updateAttribute(i, 'key', e.target.value)} className="form-control" />
                    <input type="text" placeholder="Value" value={a.value} onChange={(e) => updateAttribute(i, 'value', e.target.value)} className="form-control" />
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => removeAttribute(i)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* SHIPPING */}
            <div className="form-section">
              <h3 className="form-section-title">Shipping</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Weight (g)</label>
                  <input type="number" name="weight" value={formData.weight} onChange={handleChange} className="form-control" min="0" />
                </div>
                <div className="form-group">
                  <label className="form-label">Shipping Charge (₹)</label>
                  <input type="number" name="shippingCharge" value={formData.shippingCharge} onChange={handleChange} className="form-control" min="0" />
                </div>
                <div className="form-group">
                  <label className="form-label">Delivery Time</label>
                  <input type="text" name="deliveryTime" value={formData.deliveryTime} onChange={handleChange} className="form-control" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Length (cm)</label>
                  <input type="number" value={formData.dimensions.length} onChange={(e) => handleNestedChange('dimensions', 'length', e.target.value)} className="form-control" min="0" />
                </div>
                <div className="form-group">
                  <label className="form-label">Width (cm)</label>
                  <input type="number" value={formData.dimensions.width} onChange={(e) => handleNestedChange('dimensions', 'width', e.target.value)} className="form-control" min="0" />
                </div>
                <div className="form-group">
                  <label className="form-label">Height (cm)</label>
                  <input type="number" value={formData.dimensions.height} onChange={(e) => handleNestedChange('dimensions', 'height', e.target.value)} className="form-control" min="0" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" name="freeShipping" checked={formData.freeShipping} onChange={handleChange} />
                    Free Shipping
                  </label>
                </div>
                <div className="form-group">
                  <label className="form-label">Shipping Type</label>
                  <select name="shippingType" value={formData.shippingType} onChange={handleChange} className="form-control">
                    <option value="standard">Standard</option>
                    <option value="express">Express</option>
                    <option value="same_day">Same Day</option>
                  </select>
                </div>
              </div>
            </div>

            {/* RETURN */}
            <div className="form-section">
              <h3 className="form-section-title">Return & Warranty</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Return Days</label>
                  <input type="number" name="returnDays" value={formData.returnDays} onChange={handleChange} className="form-control" min="0" />
                </div>
                <div className="form-group">
                  <label className="form-label">Warranty Period (months)</label>
                  <input type="number" name="warrantyPeriod" value={formData.warrantyPeriod} onChange={handleChange} className="form-control" min="0" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Warranty Text</label>
                <input type="text" name="warranty" value={formData.warranty} onChange={handleChange} className="form-control" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" name="returnable" checked={formData.returnable} onChange={handleChange} />
                    Returnable
                  </label>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" name="replacement" checked={formData.replacement} onChange={handleChange} />
                    Replacement Available
                  </label>
                </div>
              </div>
            </div>

            {/* SEO */}
            <div className="form-section">
              <h3 className="form-section-title">SEO</h3>
              <div className="form-group">
                <label className="form-label">Meta Title</label>
                <input type="text" name="metaTitle" value={formData.metaTitle} onChange={handleChange} className="form-control" />
              </div>
              <div className="form-group">
                <label className="form-label">Meta Description</label>
                <textarea name="metaDescription" value={formData.metaDescription} onChange={handleChange} className="form-control" rows="2" />
              </div>
              <div className="form-group">
                <label className="form-label">Meta Keywords</label>
                <input type="text" name="metaKeywords" value={formData.metaKeywords} onChange={handleChange} className="form-control" placeholder="comma, separated" />
              </div>
            </div>

            {/* OFFERS */}
            <div className="form-section">
              <h3 className="form-section-title">Offers & Sale</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Offer Text</label>
                  <input type="text" name="offerText" value={formData.offerText} onChange={handleChange} className="form-control" />
                </div>
                <div className="form-group">
                  <label className="form-label">Coupon Code</label>
                  <input type="text" name="couponCode" value={formData.couponCode} onChange={handleChange} className="form-control" />
                </div>
              </div>
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={formData.flashSale.isActive}
                  onChange={(e) => handleNestedChange('flashSale', 'isActive', e.target.checked)}
                />
                Enable Flash Sale
              </label>
              {formData.flashSale.isActive && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Sale Price (₹)</label>
                    <input type="number" value={formData.flashSale.salePrice} onChange={(e) => handleNestedChange('flashSale', 'salePrice', e.target.value)} className="form-control" min="0" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Start Date</label>
                    <input type="date" value={formData.flashSale.startDate} onChange={(e) => handleNestedChange('flashSale', 'startDate', e.target.value)} className="form-control" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">End Date</label>
                    <input type="date" value={formData.flashSale.endDate} onChange={(e) => handleNestedChange('flashSale', 'endDate', e.target.value)} className="form-control" />
                  </div>
                </div>
              )}
            </div>

            {/* MANUFACTURER */}
            <div className="form-section">
              <h3 className="form-section-title">Manufacturer</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Manufacturer Name</label>
                  <input type="text" name="manufacturerName" value={formData.manufacturerName} onChange={handleChange} className="form-control" />
                </div>
                <div className="form-group">
                  <label className="form-label">HSN Code</label>
                  <input type="text" name="hsnCode" value={formData.hsnCode} onChange={handleChange} className="form-control" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Manufacturer Address</label>
                <textarea name="manufacturerAddress" value={formData.manufacturerAddress} onChange={handleChange} className="form-control" rows="2" />
              </div>
              <div className="form-group">
                <label className="form-label">Country of Origin</label>
                <input type="text" name="countryOfOrigin" value={formData.countryOfOrigin} onChange={handleChange} className="form-control" />
              </div>
            </div>

            {/* FLAGS */}
            <div className="form-section">
              <h3 className="form-section-title">Flags & Status</h3>
              <div className="checkbox-grid">
                {[
                  { name: 'isActive', label: 'Active' },
                  { name: 'isFeatured', label: 'Featured' },
                  { name: 'isTrending', label: 'Trending' },
                  { name: 'isNewArrival', label: 'New Arrival' },
                  { name: 'isBestSeller', label: 'Best Seller' },
                  { name: 'isRecommended', label: 'Recommended' },
                  { name: 'approvedByAdmin', label: 'Approved by Admin' },
                ].map((flag) => (
                  <label key={flag.name} className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" name={flag.name} checked={formData[flag.name]} onChange={handleChange} />
                    {flag.label}
                  </label>
                ))}
              </div>
              <div className="form-group" style={{ marginTop: 16 }}>
                <label className="form-label">Status</label>
                <select name="status" value={formData.status} onChange={handleChange} className="form-control">
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            <div className="form-actions">
              <Link to="/products" className="btn btn-outline">Cancel</Link>
              <button type="submit" className="btn btn-primary" disabled={saving || catLoading}>
                {saving ? 'Saving...' : 'Save Product'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default ProductAdd;